import React, { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_PREFIX = "mew_split_";
const MIN_PERCENT = 20;
const MAX_PERCENT = 85;

interface ResizableSplitProps {
  storageKey?: string;
  initialPercent?: number;
  children: [React.ReactElement, React.ReactElement];
  className?: string;
}

export default function ResizableSplit({
  storageKey,
  initialPercent = 65,
  children,
  className = "",
}: ResizableSplitProps) {
  const [left, right] = React.Children.toArray(children);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const [percent, setPercent] = useState<number>(() => {
    if (!storageKey) return initialPercent;
    const stored = localStorage.getItem(STORAGE_PREFIX + storageKey);
    const n = stored ? parseFloat(stored) : NaN;
    return Number.isFinite(n) ? n : initialPercent;
  });

  useEffect(() => {
    if (storageKey) localStorage.setItem(STORAGE_PREFIX + storageKey, String(percent));
  }, [percent, storageKey]);

  const onMove = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 100;
    const clamped = Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, raw));
    setPercent(clamped);
  }, []);

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (!draggingRef.current) return;
      e.preventDefault();
      onMove(e.clientX);
    }
    function handleTouchMove(e: TouchEvent) {
      if (!draggingRef.current || !e.touches[0]) return;
      onMove(e.touches[0].clientX);
    }
    function stop() {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    }
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", stop);
    };
  }, [onMove]);

  function startDrag(e: React.MouseEvent | React.TouchEvent) {
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    if ("touches" in e && e.touches[0]) onMove(e.touches[0].clientX);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowLeft") setPercent((p) => Math.max(MIN_PERCENT, p - 2));
    else if (e.key === "ArrowRight") setPercent((p) => Math.min(MAX_PERCENT, p + 2));
  }

  return (
    <div ref={containerRef} className={`flex w-full h-full ${className}`}>
      <div style={{ width: `${percent}%` }} className="min-w-0 flex flex-col overflow-hidden">
        {left}
      </div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={Math.round(percent)}
        tabIndex={0}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        onKeyDown={handleKeyDown}
        className="group relative w-1.5 cursor-col-resize bg-gray-200 hover:bg-pink-400 transition-colors focus:outline-none focus:bg-pink-500"
        title="Drag to resize"
      >
        <span className="absolute inset-y-0 -left-1 -right-1" />
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-400 group-hover:text-white text-[10px]">
          <i className="fa-solid fa-grip-lines-vertical" aria-hidden="true" />
        </span>
      </div>
      <div style={{ width: `${100 - percent}%` }} className="min-w-0 flex flex-col overflow-hidden">
        {right}
      </div>
    </div>
  );
}
